import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Col, Form, Input } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import CustomizerContext from '../../../../_helper/Customizer';
import i18n from '../../../../i18n';
import {
  formatDashboardCurrency,
  formatDashboardKpiNumber,
  formatReportingFreshnessLabel,
  loadDashboardActivityData,
  loadDashboardKpiData,
} from '../../MainPage/activityData';
import { loadVehicleNoticeAlerts } from '../../VehicleAlerts/vehicleAlertsData';
import {
  extractDirectSearchTerm,
  getAssistantIntent,
  normalizeAssistantQuery,
} from './assistantIntent';
import { loadAiChatResponse } from './assistantApi';

const QUICK_PROMPTS = [
  'Vehicules a surveiller',
  'Avis emis dernieres 24 h',
  'Paiements confirmes',
];

const getDashboardPath = path => `${String(process.env.PUBLIC_URL || '').replace(/\/+$/, '')}${path}`;

const getActivityLabel = activity => {
  const ticket = activity.ticketNumber ? ` #${activity.ticketNumber}` : '';
  const client = activity.clientName ? ` - ${activity.clientName}` : '';
  return `${activity.type}${ticket}${client}`;
};

const getKpiReport = () => loadDashboardKpiData(new Date(), {
  skipCache: true,
  threshold: 5,
});

const getActivityReport = () => loadDashboardActivityData(new Date(), {
  skipCache: true,
});

const buildAssistantResponse = async (query) => {
  const intent = getAssistantIntent(query);

  if (intent === 'vehicle-alerts') {
    const report = await loadVehicleNoticeAlerts({ limit: 5 });
    const topAlerts = report.alerts.slice(0, 3).map(alert => (
      `${alert.plate}: ${formatDashboardKpiNumber(alert.noticeCount)} avis`
    ));

    return {
      title: 'Vehicules a surveiller',
      message: `${formatDashboardKpiNumber(report.totalVehicles)} vehicules ont ${report.threshold} avis ou plus.`,
      details: topAlerts,
      actionLabel: 'Voir le rapport',
      actionPath: '/dashboard/alertes-vehicules',
    };
  }

  if (intent === 'payments') {
    const report = await getKpiReport();
    const payments = report.paymentConfirmations;

    return {
      title: 'Paiements confirmes',
      message: `${formatDashboardKpiNumber(payments.last24hTotal)} avis payes sur 24 h, pour ${formatDashboardCurrency(payments.last24hAmount)}.`,
      details: [
        `${formatDashboardKpiNumber(payments.previous24hTotal)} avis payes sur les 24 h precedentes`,
        `${formatDashboardCurrency(payments.previous24hAmount)} sur les 24 h precedentes`,
      ],
    };
  }

  if (intent === 'contestations') {
    const report = await getKpiReport();
    const contestations = report.contestations;

    return {
      title: 'Contestations',
      message: `${formatDashboardKpiNumber(contestations.last24hTotal)} contestations sur les dernieres 24 h.`,
      details: [
        `${formatDashboardKpiNumber(contestations.previous24hTotal)} contestations sur les 24 h precedentes`,
        `${formatDashboardKpiNumber(contestations.last24hMatchedNoticeCount)} avis associes`,
      ],
    };
  }

  if (intent === 'freshness') {
    const report = await getKpiReport();
    const freshness = report.freshness;

    return {
      title: 'Fraicheur reporting',
      message: formatReportingFreshnessLabel(freshness),
      details: [
        freshness.latestNoticeSyncAt ? `Derniere sync avis: ${freshness.latestNoticeSyncAt}` : '',
        freshness.latestEmailSeenAt ? `Dernier courriel lu: ${freshness.latestEmailSeenAt}` : '',
      ].filter(Boolean),
    };
  }

  if (intent === 'activity') {
    const report = await getActivityReport();

    return {
      title: 'Activite',
      message: `${formatDashboardKpiNumber(report.currentSevenDayTotal)} avis emis sur 7 jours, vs ${formatDashboardKpiNumber(report.previousSevenDayTotal)} sur les 7 jours precedents.`,
      details: report.activities.slice(0, 3).map(getActivityLabel),
    };
  }

  if (intent === 'issued-notices') {
    const report = await getKpiReport();
    const issuedTickets = report.issuedTickets;

    return {
      title: 'Avis emis',
      message: `${formatDashboardKpiNumber(issuedTickets.last24hTotal)} avis emis sur les dernieres 24 h.`,
      details: [
        `${formatDashboardKpiNumber(issuedTickets.previous24hTotal)} avis sur les 24 h precedentes`,
        `${formatDashboardKpiNumber(issuedTickets.currentSevenDayTotal)} avis sur les 7 derniers jours`,
      ],
    };
  }

  return {
    title: 'MaxPark AI',
    message: 'Je peux rechercher une plaque ou un avis, et repondre aux questions dashboard connectees aux rapports Postgres.',
    details: [
      'Exemples: plaque 13NAFL, avis 6673613, paiements confirmes, vehicules a surveiller.',
    ],
  };
};

const Searchbar = ({ searchtoggle }) => {
  const navigate = useNavigate();
  const [assistantValue, setAssistantValue] = useState('');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [response, setResponse] = useState(null);
  const { setIsClose } = useContext(CustomizerContext);

  const closeAssistant = useCallback(() => {
    setAssistantValue('');
    setIsPanelOpen(false);
    setStatus('idle');
    setResponse(null);
    setIsClose(false);
    searchtoggle(false);
  }, [searchtoggle, setIsClose]);

  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      closeAssistant();
    }
  }, [closeAssistant]);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const navigateToSearch = useCallback((searchTerm) => {
    const normalizedSearchValue = normalizeAssistantQuery(searchTerm);

    if (!normalizedSearchValue) {
      return false;
    }

    setIsPanelOpen(false);
    searchtoggle(false);
    return navigate(`/dashboard/search/${encodeURIComponent(normalizedSearchValue)}`);
  }, [navigate, searchtoggle]);

  const submitAssistantQuery = useCallback(async (overrideValue = '') => {
    const query = normalizeAssistantQuery(overrideValue || assistantValue);

    if (!query) {
      return false;
    }

    const searchTerm = extractDirectSearchTerm(query);

    if (searchTerm) {
      return navigateToSearch(searchTerm);
    }

    setAssistantValue(query);
    setIsPanelOpen(true);
    setStatus('loading');
    setResponse({
      title: 'MaxPark AI',
      message: i18n.t('Dashboard_Header_AILoading'),
      details: [],
    });

    try {
      let assistantResponse;

      try {
        assistantResponse = await loadAiChatResponse(query);
      } catch (serverError) {
        assistantResponse = await buildAssistantResponse(query);
      }

      setResponse(assistantResponse);
      setStatus('ready');
      return true;
    } catch (error) {
      setResponse({
        title: 'MaxPark AI',
        message: i18n.t('Dashboard_Header_AIError'),
        details: [error.message].filter(Boolean),
      });
      setStatus('error');
      return false;
    }
  }, [assistantValue, navigateToSearch]);

  const handleSubmit = async event => {
    event.preventDefault();
    return submitAssistantQuery();
  };

  const handleQuickPrompt = prompt => {
    setAssistantValue(prompt);
    submitAssistantQuery(prompt);
  };

  const setSearchFocus = () => {
    searchtoggle(true);
    setIsPanelOpen(true);
  };

  const removeSearchFocus = () => {
    searchtoggle(false);
  };

  return (
    <Fragment>
      <Col className="left-menu-header maxpark-ai-header">
        <Form className="maxpark-ai-form" onSubmit={handleSubmit} method="get">
          <div className="maxpark-ai-box">
            <i className="fa fa-commenting-o maxpark-ai-icon" aria-hidden="true"></i>
            <Input
              aria-label={i18n.t('Dashboard_Header_AILabel')}
              className="maxpark-ai-input"
              type="text"
              placeholder={i18n.t('Dashboard_Header_AIPlaceholder')}
              value={assistantValue}
              onChange={(event) => setAssistantValue(event.target.value)}
              onFocus={setSearchFocus}
              onBlur={removeSearchFocus}
            />
            <button
              aria-label={i18n.t('Dashboard_Header_AISubmit')}
              className="maxpark-ai-submit"
              disabled={status === 'loading'}
              type="submit"
            >
              <i className="fa fa-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </Form>
        {isPanelOpen && (
          <div className="maxpark-ai-panel">
            {!response && (
              <div className="maxpark-ai-prompts">
                {QUICK_PROMPTS.map(prompt => (
                  <button
                    className="maxpark-ai-prompt"
                    key={prompt}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleQuickPrompt(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            {response && (
              <div className={`maxpark-ai-response maxpark-ai-response-${status}`}>
                <div className="maxpark-ai-response-title">{response.title}</div>
                <div className="maxpark-ai-response-message">{response.message}</div>
                {(response.details || []).length > 0 && (
                  <ul className="maxpark-ai-response-details">
                    {response.details.map(detail => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                )}
                {response.actionPath && (
                  <button
                    className="maxpark-ai-action"
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => navigate(getDashboardPath(response.actionPath))}
                  >
                    {response.actionLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Col>
    </Fragment>
  );
};

export default Searchbar;
